import { Scenario, getRiskConsequencesName, getRiskEventsName, getRiskSourcesName } from 'src/api/types';
import { formatDate, sanitizeForCSV } from 'src/utils/misc';
import { ScenarioCsvHeaders } from 'src/api/types/misc';
import { AssessmentQuery } from 'src/graphql/_generated';
import { getIncompleteStateNew } from 'src/common';
import { auditEvent } from 'src/api/other';
import { gaEventEXPORT } from 'src/utils/gaEvent';
import Papa from 'papaparse';

type AssessmentScenario = NonNullable<AssessmentQuery['assessment']>['scenarios'][number];

const getCSVData = (scenarios: AssessmentScenario[]) => {
  const headers = [
    ScenarioCsvHeaders.Name,
    ScenarioCsvHeaders.ID,
    [ScenarioCsvHeaders.Status],
    ScenarioCsvHeaders.Sources,
    ScenarioCsvHeaders.Events,
    ScenarioCsvHeaders.Consequences,
    ScenarioCsvHeaders.RiskOwner,
    ScenarioCsvHeaders.RiskNarrative,
    'Unmodified Expected Loss',
    'Live Expected Loss',
    'Projected Expected Loss',
    ScenarioCsvHeaders.UnmodifiedFrequencyTimes,
    ScenarioCsvHeaders.UnmodifiedFrequencyYears,
    ScenarioCsvHeaders.LiveFrequencyTimes,
    ScenarioCsvHeaders.LiveFrequencyYears,
    ScenarioCsvHeaders.ProjectedFrequencyTimes,
    ScenarioCsvHeaders.ProjectedFrequencyYears,
    ScenarioCsvHeaders.FrequencyNote,
    ScenarioCsvHeaders.UnmodifiedConsequenceLower,
    ScenarioCsvHeaders.UnmodifiedConsequenceUpper,
    ScenarioCsvHeaders.LiveConsequenceLower,
    ScenarioCsvHeaders.LiveConsequenceUpper,
    ScenarioCsvHeaders.ProjectedConsequenceLower,
    ScenarioCsvHeaders.ProjectedConsequenceUpper,
    ScenarioCsvHeaders.ConsequenceNote,
    ScenarioCsvHeaders.Created,
    ScenarioCsvHeaders.Updated,
    ScenarioCsvHeaders.IdentifiedDate,
    ScenarioCsvHeaders.ReviewDate,
    ScenarioCsvHeaders.RiskManagementStrategy,
    ScenarioCsvHeaders.RiskManagementNote,
  ];

  return [
    headers,
    ...scenarios.map((el) => {
      const liveItem = el.liveRisk;
      const projectedItem = el.projectedRisk;
      const isIncompletetech = getIncompleteStateNew(el) === 'TECH';

      return [
        sanitizeForCSV(el.name), // Name
        sanitizeForCSV(el.id), // ID
        sanitizeForCSV(el.status), // Status (conditional)
        sanitizeForCSV(getRiskSourcesName(el.source)), // Sources
        sanitizeForCSV(getRiskEventsName(el.event)), // Events
        sanitizeForCSV(getRiskConsequencesName(el.consequence)), // Consequences
        sanitizeForCSV(el.riskOwner?.text), // Risk Owner
        sanitizeForCSV(el.riskNarrative), // Risk Narrative
        isIncompletetech ? '' : Math.round(el.baseRisk?.expectedLoss || 0), // Unmodified Expected Loss
        isIncompletetech ? '' : liveItem ? Math.round(liveItem.expectedLoss || 0) : '', // Live Expected Loss
        isIncompletetech ? '' : projectedItem ? Math.round(projectedItem.expectedLoss || 0) : '', // Projected Expected Loss
        sanitizeForCSV(el.baseRisk?.frequencyTimes), // Unmodified Frequency Times
        sanitizeForCSV(el.baseRisk?.frequencyYears), // Unmodified Frequency Years
        isIncompletetech ? '' : sanitizeForCSV(liveItem?.frequencyTimes), // Live Frequency Times
        isIncompletetech ? '' : sanitizeForCSV(liveItem?.frequencyYears), // Live Frequency Years
        isIncompletetech ? '' : sanitizeForCSV(projectedItem?.frequencyTimes), // Projected Frequency Times
        isIncompletetech ? '' : sanitizeForCSV(projectedItem?.frequencyYears), // Projected Frequency Years
        sanitizeForCSV(el.frequencyNote), // Frequency Note
        Math.round(el.baseRisk?.lower || 0), // Unmodified Consequence Lower
        Math.round(el.baseRisk?.upper || 0), // Unmodified Consequence Upper
        isIncompletetech ? '' : liveItem ? Math.round(liveItem?.lower || 0) : '', // Live Consequence Lower
        isIncompletetech ? '' : liveItem ? Math.round(liveItem.upper || 0) : '', // Live Consequence Upper
        isIncompletetech ? '' : projectedItem ? Math.round(projectedItem.lower || 0) : '', // Projected Consequence Lower
        isIncompletetech ? '' : projectedItem ? Math.round(projectedItem.upper || 0) : '', // Projected Consequence Upper
        sanitizeForCSV(el.consequenceNote), // Consequence Note
        formatDate(el.meta?.createdAt, { formatType: 'datetime' }), // Created
        formatDate(el.meta?.updatedAt, { formatType: 'datetime' }), // Updated
        formatDate(el.riskIdentifiedDate), // Identified Date
        formatDate(el.reviewDate), // Review Date
        sanitizeForCSV(el.managementStrategy), // Risk Management Strategy
        sanitizeForCSV(el.managementNote), // Risk Management Note
      ];
    }),
  ];
};

type DownloadCsv = (params: {
  data: NonNullable<AssessmentQuery['assessment']>;
  onDone: () => void;
  includeStatus?: boolean;
}) => void;

export const downloadCsv: DownloadCsv = ({ data, onDone }) => {
  const rows = getCSVData(data.scenarios || []);
  const csvString = Papa.unparse(rows, {
    header: true,
  });
  const anchor = document.createElement('a');
  anchor.href = 'data:attachment/csv;charset=utf-8,' + encodeURIComponent(csvString);
  anchor.target = '_blank';
  anchor.download = `${data?.name}.csv`;
  anchor.innerHTML = 'test';
  gaEventEXPORT('EXPORT_CSV');

  auditEvent({
    action: 'ExportCsv',
    resource: 'assessment',
    assessment_id: data.id,
  }).then(onDone);

  anchor.click();
};

export interface VariantsScenario extends Omit<Scenario, 'expected_loss'> {
  expected_loss: {
    base: number;
    live: number;
    projected: number;
  };
}
